import type { MarkupElement, MarkupData, ResizeHandle } from '../types/database'

export interface Point {
  x: number
  y: number
}

interface Bounds {
  x: number
  y: number
  width: number
  height: number
}

const HIT_THRESHOLD = 5 // percentage units

/**
 * Split text into lines that fit within a max pixel width.
 */
export function wrapText(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number
): string[] {
  const words = text.split(' ')
  const lines: string[] = []
  let currentLine = ''

  for (const word of words) {
    // If a single word is too wide, break it by character
    if (ctx.measureText(word).width > maxWidth && !currentLine) {
      let partial = ''
      for (const char of word) {
        if (ctx.measureText(partial + char).width > maxWidth && partial) {
          lines.push(partial)
          partial = char
        } else {
          partial += char
        }
      }
      currentLine = partial
      continue
    }

    const testLine = currentLine ? `${currentLine} ${word}` : word
    if (ctx.measureText(testLine).width > maxWidth && currentLine) {
      lines.push(currentLine)
      currentLine = word
    } else {
      currentLine = testLine
    }
  }
  if (currentLine) {
    lines.push(currentLine)
  }
  return lines.length > 0 ? lines : ['']
}
const HANDLE_SIZE = 3 // percentage units

// Arrowhead length in px. Shared by the editor and the bake path — these were
// 15 and 20 respectively until 2026-07-28, so saved images didn't match what
// the user drew. See computeLabelBlock for the same story on label font size.
export const ARROW_HEAD_LEN = 15
const LABEL_MIN_FONT_PX = 5
const TEXT_BLOCK_WIDTH_DEFAULT = 35

// Nominal editor canvas width in px. Everything else in this system is stored
// as a percentage of canvas width; `strokeWidth` is the one legacy raw-pixel
// value, and stored values (2/4/6/8) mean "px on an editor canvas about this
// wide". The same constant is implied by getElementBounds' legacy fontSize/4
// conversion, which assumes a ~400px canvas.
const NOMINAL_CANVAS_WIDTH = 400

/**
 * Convert a stored strokeWidth to canvas pixels.
 *
 * Fixes an editor-vs-bake divergence reported 2026-07-28: both paths assigned
 * `ctx.lineWidth = element.strokeWidth` unscaled, but the editor canvas is
 * screen-sized (~390px on an iPhone) while the bake canvas is the full source
 * image (1200px via web_path, up to 4032px for an offline capture). A 4px line
 * was ~1% of the editor's width but ~0.33% of the saved photo's — so saved
 * markup came out visibly thinner than what was drawn.
 *
 * Scaling by canvas width makes stroke behave like every other dimension here.
 * The 1px floor keeps lines visible when baking into small derivatives.
 */
export function strokeWidthPx(strokeWidth: number, canvasWidth: number): number {
  return Math.max(1, scaleFromNominal(strokeWidth, canvasWidth))
}

/**
 * Scale a nominal-canvas pixel measurement to the actual canvas width.
 *
 * Only for marks that get baked into the image. Editor-only chrome (selection
 * handles, the move indicator) must stay in absolute pixels — it's sized for a
 * fingertip on a screen, not for the photo.
 */
export function scaleFromNominal(px: number, canvasWidth: number): number {
  return px * (canvasWidth / NOMINAL_CANVAS_WIDTH)
}

/**
 * Arrowhead length in canvas pixels.
 *
 * Must scale with the same factor as strokeWidthPx: a 4px stroke becomes 12px
 * on a 1200px bake, so a fixed 15px head would be barely wider than its own
 * line and read as a broken arrow.
 */
export function arrowHeadLenPx(canvasWidth: number): number {
  return scaleFromNominal(ARROW_HEAD_LEN, canvasWidth)
}

export interface LabelBlock {
  /** Top-left of the text bounding box, in px. */
  x: number
  y: number
  w: number
  h: number
  /** Baseline origin of the first line, in px. */
  drawX: number
  drawY: number
  lines: string[]
  fontSize: number
  lineHeight: number
  /** Unit perpendicular to the arrow — which side the block sits on. */
  perpX: number
  perpY: number
}

export interface TextBlock {
  x: number
  y: number
  w: number
  h: number
  fontSize: number
  lines: string[]
  lineHeight: number
}

/**
 * Geometry of an arrow's label block, in canvas pixels.
 *
 * Single source of truth for three callers that must agree: the editor's
 * render, the editor's hit-testing, and the bake path. They had drifted apart
 * (bake used a 7px minimum font, editor 5px), so a saved photo didn't match
 * the editor. Extracted from MarkupEditor 2026-07-28.
 *
 * Mutates `ctx.font` as a side effect of measuring — same contract as wrapText.
 */
export function computeLabelBlock(
  el: MarkupElement,
  W: number,
  H: number,
  ctx: CanvasRenderingContext2D
): LabelBlock | null {
  if (!el.points || el.points.length < 2 || !el.label) return null
  const tip = el.points[0]
  const tail = el.points[el.points.length - 1]
  const tailXpx = (tail.x / 100) * W
  const tailYpx = (tail.y / 100) * H
  const tipXpx = (tip.x / 100) * W
  const tipYpx = (tip.y / 100) * H
  const dx = tipXpx - tailXpx
  const dy = tipYpx - tailYpx
  const len = Math.sqrt(dx * dx + dy * dy) || 1
  const perpX = -dy / len
  const perpY = dx / len
  const fontSize = Math.max(LABEL_MIN_FONT_PX, ((el.labelFontSize || LABEL_FONT_DEFAULT) / 100) * W)
  const blockWidth = ((el.labelWidth || LABEL_WIDTH_DEFAULT) / 100) * W
  const noteOffset = fontSize * 0.8
  const originX = tailXpx + perpX * noteOffset
  const originY = tailYpx + perpY * noteOffset
  ctx.font = `bold ${fontSize}px Arial`
  const lines = wrapText(ctx, el.label, blockWidth)
  const lineHeight = fontSize * 1.3
  const blockHeight = lines.length * lineHeight

  // Position the block so its near edge touches the origin and it extends AWAY
  // from the arrow, so the label never overlaps the line it belongs to.
  const drawX = perpX >= 0 ? originX : originX - blockWidth
  const drawY = perpY >= 0 ? originY + fontSize : originY - (lines.length - 1) * lineHeight

  return {
    x: drawX,
    y: drawY - fontSize,
    w: blockWidth,
    h: blockHeight,
    drawX,
    drawY,
    lines,
    fontSize,
    lineHeight,
    perpX,
    perpY,
  }
}

/**
 * Text element font size in px.
 *
 * Note the truthy check on `textFontSize`: an explicit 0 falls through to the
 * legacy raw-pixel `fontSize`. That differs from resizeTextElement(), which
 * uses `??` and honours an explicit 0. Both behaviors are inherited from the
 * original code and are pinned by tests — do not "unify" them without deciding
 * which is correct and checking real markup rows.
 */
export function resolveTextFontSize(el: MarkupElement, W: number): number {
  if (el.textFontSize) return (el.textFontSize / 100) * W
  return el.fontSize || LEGACY_FONT_SIZE_PX
}

/** Geometry of a text element's block, in canvas pixels. */
export function computeTextBlock(
  el: MarkupElement,
  W: number,
  H: number,
  ctx: CanvasRenderingContext2D
): TextBlock {
  const fontSize = resolveTextFontSize(el, W)
  ctx.font = `bold ${fontSize}px Arial`
  const blockW = ((el.textWidth || TEXT_BLOCK_WIDTH_DEFAULT) / 100) * W
  const lines = wrapText(ctx, el.text || '', blockW)
  const lineHeight = fontSize * 1.3
  return {
    x: (el.x! / 100) * W,
    y: (el.y! / 100) * H - fontSize,
    w: blockW,
    h: lines.length * lineHeight,
    fontSize,
    lines,
    lineHeight,
  }
}

/**
 * Get the bounding box of any markup element
 */
export function getElementBounds(element: MarkupElement): Bounds {
  switch (element.type) {
    case 'arrow':
    case 'freehand':
      if (!element.points || element.points.length === 0) {
        return { x: 0, y: 0, width: 0, height: 0 }
      }
      const xs = element.points.map(p => p.x)
      const ys = element.points.map(p => p.y)
      const minX = Math.min(...xs)
      const maxX = Math.max(...xs)
      const minY = Math.min(...ys)
      const maxY = Math.max(...ys)
      return {
        x: minX,
        y: minY,
        width: maxX - minX,
        height: maxY - minY,
      }

    case 'rectangle':
    case 'circle':
      // Handle negative width/height from drawing in different directions
      const x = element.width! < 0 ? element.x! + element.width! : element.x!
      const y = element.height! < 0 ? element.y! + element.height! : element.y!
      return {
        x,
        y,
        width: Math.abs(element.width!),
        height: Math.abs(element.height!),
      }

    case 'text': {
      // fontSizePct: percentage of canvas width (used for both hit-test and selection box)
      const fontSizePct = element.textFontSize ?? ((element.fontSize || 24) / 4) // legacy: px→% assuming ~400px canvas
      const widthPct = element.textWidth ?? Math.max((element.text?.length || 0) * fontSizePct * 0.6, 5)
      const charsPerLine = Math.max(1, widthPct / (fontSizePct * 0.6))
      const lineCount = Math.ceil((element.text?.length || 1) / charsPerLine)
      const lineHeightPct = fontSizePct * 1.3
      return {
        x: element.x! - 0.5,
        y: element.y! - fontSizePct,
        width: widthPct,
        height: lineCount * lineHeightPct,
      }
    }

    default:
      return { x: 0, y: 0, width: 0, height: 0 }
  }
}

/**
 * Check if a point hits an element
 */
export function hitTestElement(element: MarkupElement, point: Point, threshold = HIT_THRESHOLD): boolean {
  switch (element.type) {
    case 'arrow':
      if (!element.points || element.points.length < 2) return false
      const start = element.points[0]
      const end = element.points[element.points.length - 1]
      return distanceToLineSegment(point, start, end) < threshold

    case 'freehand':
      if (!element.points || element.points.length < 2) return false
      for (let i = 0; i < element.points.length - 1; i++) {
        if (distanceToLineSegment(point, element.points[i], element.points[i + 1]) < threshold) {
          return true
        }
      }
      return false

    case 'rectangle': {
      const bounds = getElementBounds(element)
      // Check if point is on the border (not inside)
      const onLeft = Math.abs(point.x - bounds.x) < threshold && point.y >= bounds.y - threshold && point.y <= bounds.y + bounds.height + threshold
      const onRight = Math.abs(point.x - (bounds.x + bounds.width)) < threshold && point.y >= bounds.y - threshold && point.y <= bounds.y + bounds.height + threshold
      const onTop = Math.abs(point.y - bounds.y) < threshold && point.x >= bounds.x - threshold && point.x <= bounds.x + bounds.width + threshold
      const onBottom = Math.abs(point.y - (bounds.y + bounds.height)) < threshold && point.x >= bounds.x - threshold && point.x <= bounds.x + bounds.width + threshold
      return onLeft || onRight || onTop || onBottom
    }

    case 'circle': {
      const bounds = getElementBounds(element)
      const centerX = bounds.x + bounds.width / 2
      const centerY = bounds.y + bounds.height / 2
      const radiusX = bounds.width / 2
      const radiusY = bounds.height / 2
      // Check if point is on the ellipse border
      const dx = (point.x - centerX) / radiusX
      const dy = (point.y - centerY) / radiusY
      const distance = Math.sqrt(dx * dx + dy * dy)
      return Math.abs(distance - 1) < threshold / Math.max(radiusX, radiusY, 1)
    }

    case 'text': {
      const bounds = getElementBounds(element)
      return (
        point.x >= bounds.x - threshold &&
        point.x <= bounds.x + bounds.width + threshold &&
        point.y >= bounds.y - threshold &&
        point.y <= bounds.y + bounds.height + threshold
      )
    }

    default:
      return false
  }
}

/**
 * Check which resize handle is at a point (if any)
 */
export function getResizeHandle(element: MarkupElement, point: Point): ResizeHandle | null {
  // Arrows: endpoints + optional label resize handle
  if (element.type === 'arrow' && element.points && element.points.length >= 2) {
    const p0 = element.points[0]
    const p1 = element.points[element.points.length - 1]
    if (Math.abs(point.x - p0.x) < HANDLE_SIZE && Math.abs(point.y - p0.y) < HANDLE_SIZE) return 'p0'
    if (Math.abs(point.x - p1.x) < HANDLE_SIZE && Math.abs(point.y - p1.y) < HANDLE_SIZE) return 'p1'
    return null
  }

  const bounds = getElementBounds(element)
  const handles: { handle: ResizeHandle; x: number; y: number }[] = [
    { handle: 'nw', x: bounds.x, y: bounds.y },
    { handle: 'n', x: bounds.x + bounds.width / 2, y: bounds.y },
    { handle: 'ne', x: bounds.x + bounds.width, y: bounds.y },
    { handle: 'e', x: bounds.x + bounds.width, y: bounds.y + bounds.height / 2 },
    { handle: 'se', x: bounds.x + bounds.width, y: bounds.y + bounds.height },
    { handle: 's', x: bounds.x + bounds.width / 2, y: bounds.y + bounds.height },
    { handle: 'sw', x: bounds.x, y: bounds.y + bounds.height },
    { handle: 'w', x: bounds.x, y: bounds.y + bounds.height / 2 },
  ]

  for (const h of handles) {
    if (Math.abs(point.x - h.x) < HANDLE_SIZE && Math.abs(point.y - h.y) < HANDLE_SIZE) {
      return h.handle
    }
  }

  return null
}

/** A block in canvas-pixel space, as returned by computeLabelBlock/computeTextBlock. */
export interface PxBlock {
  x: number
  y: number
  w: number
  h: number
}

// Hit tolerances for label/text blocks, in percentage units. The corner radius
// is deliberately larger than HANDLE_SIZE (3) because these are finger targets
// on a phone, not mouse targets.
const BLOCK_CORNER_HIT_RADIUS = 6
const BLOCK_HIT_PADDING = 2

/**
 * Which corner handle of a pixel-space block a percentage-space point hits.
 *
 * Corners are tested in nw, ne, sw, se order and the first hit wins — matching
 * the original inline loops in MarkupEditor.handleStart, where this logic was
 * duplicated three times before extraction (2026-07-28).
 */
export function hitTestBlockCorner(
  block: PxBlock,
  canvasW: number,
  canvasH: number,
  point: Point,
  prefix: 'lbl' | 'txt',
  hitRadius: number = BLOCK_CORNER_HIT_RADIUS
): ResizeHandle | null {
  const corners: { corner: 'nw' | 'ne' | 'sw' | 'se'; xPct: number; yPct: number }[] = [
    { corner: 'nw', xPct: (block.x / canvasW) * 100, yPct: (block.y / canvasH) * 100 },
    { corner: 'ne', xPct: ((block.x + block.w) / canvasW) * 100, yPct: (block.y / canvasH) * 100 },
    { corner: 'sw', xPct: (block.x / canvasW) * 100, yPct: ((block.y + block.h) / canvasH) * 100 },
    { corner: 'se', xPct: ((block.x + block.w) / canvasW) * 100, yPct: ((block.y + block.h) / canvasH) * 100 },
  ]
  for (const c of corners) {
    if (Math.abs(point.x - c.xPct) < hitRadius && Math.abs(point.y - c.yPct) < hitRadius) {
      return `${prefix}-${c.corner}` as ResizeHandle
    }
  }
  return null
}

/**
 * Whether a percentage-space point falls inside a pixel-space block, with a
 * small padding so the target is reachable with a fingertip.
 */
export function isPointInBlock(
  block: PxBlock,
  canvasW: number,
  canvasH: number,
  point: Point,
  pad: number = BLOCK_HIT_PADDING
): boolean {
  const xPct = (block.x / canvasW) * 100
  const yPct = (block.y / canvasH) * 100
  const wPct = (block.w / canvasW) * 100
  const hPct = (block.h / canvasH) * 100
  return (
    point.x >= xPct - pad &&
    point.x <= xPct + wPct + pad &&
    point.y >= yPct - pad &&
    point.y <= yPct + hPct + pad
  )
}

/**
 * Move an element by delta
 */
export function moveElement(element: MarkupElement, deltaX: number, deltaY: number): MarkupElement {
  switch (element.type) {
    case 'arrow':
    case 'freehand':
      return {
        ...element,
        points: element.points?.map(p => ({
          x: p.x + deltaX,
          y: p.y + deltaY,
        })),
      }

    case 'rectangle':
    case 'circle':
    case 'text':
      return {
        ...element,
        x: (element.x || 0) + deltaX,
        y: (element.y || 0) + deltaY,
      }

    default:
      return element
  }
}

/**
 * Resize an element from a specific handle
 */
export function resizeElement(
  element: MarkupElement,
  handle: ResizeHandle,
  deltaX: number,
  deltaY: number
): MarkupElement {
  const bounds = getElementBounds(element)

  let newX = bounds.x
  let newY = bounds.y
  let newWidth = bounds.width
  let newHeight = bounds.height

  // Adjust based on which handle is being dragged
  switch (handle) {
    case 'nw':
      newX += deltaX
      newY += deltaY
      newWidth -= deltaX
      newHeight -= deltaY
      break
    case 'n':
      newY += deltaY
      newHeight -= deltaY
      break
    case 'ne':
      newY += deltaY
      newWidth += deltaX
      newHeight -= deltaY
      break
    case 'e':
      newWidth += deltaX
      break
    case 'se':
      newWidth += deltaX
      newHeight += deltaY
      break
    case 's':
      newHeight += deltaY
      break
    case 'sw':
      newX += deltaX
      newWidth -= deltaX
      newHeight += deltaY
      break
    case 'w':
      newX += deltaX
      newWidth -= deltaX
      break
  }

  // Ensure minimum size
  const minSize = 2
  if (newWidth < minSize) {
    newWidth = minSize
    if (handle.includes('w')) newX = bounds.x + bounds.width - minSize
  }
  if (newHeight < minSize) {
    newHeight = minSize
    if (handle.includes('n')) newY = bounds.y + bounds.height - minSize
  }

  // Arrow endpoint dragging — handle is 'p0' (tip) or 'p1' (tail)
  if (element.type === 'arrow' && element.points) {
    if (handle === 'p0') {
      return {
        ...element,
        points: element.points.map((p, i) => i === 0 ? { x: p.x + deltaX, y: p.y + deltaY } : p),
      }
    }
    if (handle === 'p1') {
      const last = element.points.length - 1
      return {
        ...element,
        points: element.points.map((p, i) => i === last ? { x: p.x + deltaX, y: p.y + deltaY } : p),
      }
    }
  }

  switch (element.type) {
    case 'freehand':
      if (!element.points || element.points.length === 0) return element
      // Scale points to new bounds
      const scaleX = bounds.width > 0 ? newWidth / bounds.width : 1
      const scaleY = bounds.height > 0 ? newHeight / bounds.height : 1
      return {
        ...element,
        points: element.points.map(p => ({
          x: newX + (p.x - bounds.x) * scaleX,
          y: newY + (p.y - bounds.y) * scaleY,
        })),
      }

    case 'rectangle':
    case 'circle':
      return {
        ...element,
        x: newX,
        y: newY,
        width: newWidth,
        height: newHeight,
      }

    case 'text':
      // Text can only be moved, not resized
      return element

    default:
      return element
  }
}

// Arrow-label and text-block resize bounds, all in percentage-of-canvas-width
// units. The sensitivity factors damp vertical drag so font size changes at a
// usable rate under a finger rather than snapping between the clamps.
const LABEL_WIDTH_DEFAULT = 40
const LABEL_WIDTH_MIN = 5
const LABEL_WIDTH_MAX = 90
const LABEL_FONT_DEFAULT = 5
const LABEL_FONT_MIN = 2
const LABEL_FONT_MAX = 30
const LABEL_FONT_SENSITIVITY = 0.3

const TEXT_WIDTH_DEFAULT = 35
const TEXT_WIDTH_MIN = 10
const TEXT_WIDTH_MAX = 90
const TEXT_FONT_MIN = 1.5
const TEXT_FONT_MAX = 30
const TEXT_FONT_SENSITIVITY = 0.15
const LEGACY_FONT_SIZE_PX = 24

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value))
}

/**
 * Resize an arrow's label block: horizontal drag changes wrap width, vertical
 * drag changes font size.
 *
 * The 'lbl-*' handles never reach resizeElement() — they were computed inline
 * in MarkupEditor until this was extracted (2026-07-27), which left the clamp
 * behavior untestable. Pure extraction, no behavior change.
 */
export function resizeArrowLabel(
  element: MarkupElement,
  handle: ResizeHandle,
  deltaX: number,
  deltaY: number
): MarkupElement {
  const xFactor = handle.includes('e') ? 1 : -1
  const yFactor = handle.includes('s') ? 1 : -1
  const oldWidth = element.labelWidth || LABEL_WIDTH_DEFAULT
  const oldFontSize = element.labelFontSize || LABEL_FONT_DEFAULT
  return {
    ...element,
    labelWidth: clamp(oldWidth + deltaX * xFactor, LABEL_WIDTH_MIN, LABEL_WIDTH_MAX),
    labelFontSize: clamp(
      oldFontSize + deltaY * yFactor * LABEL_FONT_SENSITIVITY,
      LABEL_FONT_MIN,
      LABEL_FONT_MAX
    ),
  }
}

/**
 * Resize a text element: horizontal drag changes wrap width, vertical drag
 * changes font size.
 *
 * `canvasWidth` is only needed for legacy elements that still carry a raw-pixel
 * `fontSize` instead of the percentage-based `textFontSize` — it converts one
 * to the other. Note `textFontSize` is read with `??` (so an explicit 0 is
 * honoured) while `fontSize` uses `||` (so 0 falls back to 24); that asymmetry
 * is inherited from the original inline code and is deliberate.
 *
 * As with resizeArrowLabel, the 'txt-*' handles never reach resizeElement().
 */
export function resizeTextElement(
  element: MarkupElement,
  handle: ResizeHandle,
  deltaX: number,
  deltaY: number,
  canvasWidth: number
): MarkupElement {
  const xFactor = handle.includes('e') ? 1 : -1
  const yFactor = handle.includes('s') ? 1 : -1
  const currentFontSizePct =
    element.textFontSize ?? ((element.fontSize || LEGACY_FONT_SIZE_PX) / canvasWidth) * 100
  return {
    ...element,
    textFontSize: clamp(
      currentFontSizePct + deltaY * yFactor * TEXT_FONT_SENSITIVITY,
      TEXT_FONT_MIN,
      TEXT_FONT_MAX
    ),
    textWidth: clamp(
      (element.textWidth || TEXT_WIDTH_DEFAULT) + deltaX * xFactor,
      TEXT_WIDTH_MIN,
      TEXT_WIDTH_MAX
    ),
  }
}

/**
 * Calculate distance from point to line segment
 */
function distanceToLineSegment(point: Point, lineStart: Point, lineEnd: Point): number {
  const A = point.x - lineStart.x
  const B = point.y - lineStart.y
  const C = lineEnd.x - lineStart.x
  const D = lineEnd.y - lineStart.y

  const dot = A * C + B * D
  const lenSq = C * C + D * D
  let param = -1

  if (lenSq !== 0) {
    param = dot / lenSq
  }

  let xx, yy

  if (param < 0) {
    xx = lineStart.x
    yy = lineStart.y
  } else if (param > 1) {
    xx = lineEnd.x
    yy = lineEnd.y
  } else {
    xx = lineStart.x + param * C
    yy = lineStart.y + param * D
  }

  const dx = point.x - xx
  const dy = point.y - yy
  return Math.sqrt(dx * dx + dy * dy)
}

/**
 * Load an image from a URL
 */
function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    // iOS Safari can silently fail to load blob: URLs when
    // crossOrigin is set — only set it for cross-origin loads
    // (Supabase storage). This matters for the upload pipeline's
    // local markup bake, which loads from a blob URL of the
    // original captured photo.
    if (!url.startsWith('blob:')) img.crossOrigin = 'anonymous'
    img.onload = () => resolve(img)
    img.onerror = reject
    img.src = url
  })
}

/**
 * Draw a single markup element on a canvas
 */
function drawMarkupElement(
  ctx: CanvasRenderingContext2D,
  element: MarkupElement,
  width: number,
  height: number
): void {
  ctx.strokeStyle = element.color
  ctx.fillStyle = element.color
  ctx.lineWidth = strokeWidthPx(element.strokeWidth, width)
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'

  switch (element.type) {
    case 'arrow':
      if (!element.points || element.points.length < 2) return
      // Arrow direction: points[0] is tip, last point is tail
      const tip = element.points[0]
      const tail = element.points[element.points.length - 1]
      const tipX = (tip.x / 100) * width
      const tipY = (tip.y / 100) * height
      const tailX = (tail.x / 100) * width
      const tailY = (tail.y / 100) * height

      // Draw line from tail to tip
      ctx.beginPath()
      ctx.moveTo(tailX, tailY)
      ctx.lineTo(tipX, tipY)
      ctx.stroke()

      // Draw arrowhead at tip
      const angle = Math.atan2(tipY - tailY, tipX - tailX)
      const headLen = arrowHeadLenPx(width)
      ctx.beginPath()
      ctx.moveTo(tipX, tipY)
      ctx.lineTo(tipX - headLen * Math.cos(angle - Math.PI / 6), tipY - headLen * Math.sin(angle - Math.PI / 6))
      ctx.moveTo(tipX, tipY)
      ctx.lineTo(tipX - headLen * Math.cos(angle + Math.PI / 6), tipY - headLen * Math.sin(angle + Math.PI / 6))
      ctx.stroke()

      // Draw note near tail — geometry shared with the editor so the baked
      // image matches what was drawn (these had drifted; see computeLabelBlock)
      if (element.label) {
        const block = computeLabelBlock(element, width, height, ctx)
        if (block) {
          ctx.font = `bold ${block.fontSize}px Arial`
          ctx.fillStyle = element.color
          block.lines.forEach((line, i) => {
            ctx.fillText(line, block.drawX, block.drawY + i * block.lineHeight)
          })
        }
      }
      break

    case 'rectangle':
      ctx.strokeRect(
        (element.x! / 100) * width,
        (element.y! / 100) * height,
        (element.width! / 100) * width,
        (element.height! / 100) * height
      )
      break

    case 'circle':
      ctx.beginPath()
      ctx.ellipse(
        ((element.x! + element.width! / 2) / 100) * width,
        ((element.y! + element.height! / 2) / 100) * height,
        (Math.abs(element.width!) / 200) * width,
        (Math.abs(element.height!) / 200) * height,
        0,
        0,
        Math.PI * 2
      )
      ctx.stroke()
      break

    case 'freehand':
      if (!element.points || element.points.length < 2) return
      ctx.beginPath()
      element.points.forEach((p, i) => {
        const x = (p.x / 100) * width
        const y = (p.y / 100) * height
        if (i === 0) ctx.moveTo(x, y)
        else ctx.lineTo(x, y)
      })
      ctx.stroke()
      break

    case 'text': {
      const block = computeTextBlock(element, width, height, ctx)
      const baseX = (element.x! / 100) * width
      const baseY = (element.y! / 100) * height
      block.lines.forEach((line, i) =>
        ctx.fillText(line, baseX, baseY + i * block.lineHeight)
      )
      break
    }
  }
}

/**
 * Render markup elements onto an image and return the resulting canvas as a Blob
 */
export async function renderMarkupToImage(
  imageUrl: string,
  markupData: MarkupData
): Promise<Blob> {
  const img = await loadImage(imageUrl)

  // Create canvas at original image size
  const canvas = document.createElement('canvas')
  canvas.width = img.width
  canvas.height = img.height
  const ctx = canvas.getContext('2d')!

  // Draw original image
  ctx.drawImage(img, 0, 0)

  // Draw all markup elements
  if (markupData.elements && markupData.elements.length > 0) {
    markupData.elements.forEach(element => {
      drawMarkupElement(ctx, element, canvas.width, canvas.height)
    })
  }

  // Convert to blob
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob)
        } else {
          reject(new Error('Failed to create blob from canvas'))
        }
      },
      'image/jpeg',
      0.92
    )
  })
}
